import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Grid,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import {
  ApplicantDetailsPreviewResponse,
  Question,
} from "../application-preview/types";
import { ApplicantDetailsResponse } from "../application-details/types";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";
import { useNavigate } from "react-router-dom";
import { setActiveStep } from "@modules/stepper/redux/stepperSlice";
import {
  ifHillySectionRetailNew,
  ifRuralSectionPrivateCommonNew,
  ifRuralSectionPrivateNoServiceNew,
  ifRuralSectionRetailNew,
  ifUrbanSectionPrivateCommonNew,
  ifUrbanSectionPrivateNoServiceNew,
  ifUrbanSectionRetailNew,
  sectionPrivate,
  sectionsRetail,
} from "./staticAccessData";
import { resetApplicationSubmitted } from "../application-details/redux/applicationSubmittedSlice";
import { values } from "lodash";

interface StaticAccessOption {
  id: number;
  name: string;
}

interface StaticAccessQuestion {
  title: string;
  name: string;
  type: "select" | "text" | "radio";
  options?: StaticAccessOption[] | string[];
  dataType?: string;
}

interface PreviewQuestion {
  question_name: string;
  value: any;
}

type ResponseType = Record<string, any>;

export const SiteRoadwaysSection: React.FC<{
  isExpanded: boolean;
  toggleExpand: () => void;
  visible?: boolean;
  data: ApplicantDetailsPreviewResponse | ApplicantDetailsResponse;
}> = ({ isExpanded, toggleExpand, data, visible = true }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  // if (!applicationPreview) return;
  if (!data) return null;

  const buildQuestionsForPreview = (
    staticAccessData: StaticAccessQuestion[],
    response: ResponseType,
  ): PreviewQuestion[] => {
    return staticAccessData
      .filter(
        (q) =>
          response[q.name] !== undefined &&
          response[q.name] !== null &&
          response[q.name] !== "",
      )
      .map((q) => {
        // For select, get the display value (the 'name') from the option
        if (q.type === "select") {
          const option = (q.options as StaticAccessOption[])?.find((opt) =>
            typeof response[q.name] === "number"
              ? opt.id === response[q.name]
              : opt.name === response[q.name],
          );
          return {
            question_name: q.title,
            value: option ? option.name : response[q.name],
            key: q.name,
          };
        }
        // For radio, text, etc., just use the value
        return {
          question_name: q.title,
          value: response[q.name],
          key: q.name,
        };
      });
  };

  const {
    location_type_id,
    location_type_name,
    questions,
    service_road_type_id,
    service_road_type_name,
    service_road_type,
    is_coco_fuel_station,
    fuel_station_carriageway_type,
    property_type_Byid,
    property_type,
    application_category_id,
    loi_name,
    loi_dealer_name,
    loi_number,
    loi_date,
  } = data;

  const section =
    application_category_id === 2 ? sectionPrivate : sectionsRetail;

  function formatDateDDMMYY(dateString: string) {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-4);

    return `${day}/${month}/${year}`;
  }

  const getLocationSpecificSections = (): any[] => {
    if (application_category_id === 2) {
      // Private: Only Urban and Rural allowed
      switch (location_type_id) {
        case 1:
          // Add your condition here for service_road_type_id
          if (service_road_type_id === 2) {
            return ifUrbanSectionPrivateCommonNew;
          } else if (service_road_type_id === 3) {
            return ifUrbanSectionPrivateNoServiceNew;
          } else {
            return [];
          }
        case 2:
          if (service_road_type_id === 2) {
            return ifRuralSectionPrivateCommonNew;
          } else if (service_road_type_id === 3) {
            return ifRuralSectionPrivateNoServiceNew;
          } else {
            return [];
          }
        default:
          return [];
      }
    } else {
      // Retail: All options allowed
      switch (location_type_id) {
        case 1:
          return ifUrbanSectionRetailNew;
        case 2:
          return ifRuralSectionRetailNew;
        case 3:
          return ifHillySectionRetailNew;
        default:
          return [];
      }
    }
  };

  const dynamicSections: any[] = [...section, ...getLocationSpecificSections()];

  const loiDetails = [
    {
      label: "Name of LOI Issuing Authority",
      value: loi_name,
    },
    {
      label: "Name of the Dealer",
      value: loi_dealer_name,
    },
    {
      label: "LOI Number",
      value: loi_number,
    },
    {
      label: "LOI Issue Date",
      value: loi_date ? formatDateDDMMYY(loi_date) : null,
    },
  ];

  const hasLoiDetails = loiDetails?.some((item) => item.value);

  const questionsArr = buildQuestionsForPreview(dynamicSections, data);

  const roadwayData = [
  ...(service_road_type_id || service_road_type 
    ? [{
        label: "Service Road Type",
        value: service_road_type_name || service_road_type,
      }]
    : []),

  {
    label: "Select the type of fuel station allocated to the applicant",
    value:
      is_coco_fuel_station === true
        ? "COCO (Company Owned Company Operated)"
        : is_coco_fuel_station === false
          ? "Other than COCO"
          : "NA",
  },

  {
    label: "Property Type",
    value: property_type,
  },

  {
    label: "Site location",
    value: location_type_name,
  },

  ...(fuel_station_carriageway_type != null
    ? [{
        label: "Nature of Carriageway",
        value: fuel_station_carriageway_type
          ? "Undivided Carriageway"
          : "Divided Carriageway",
      }]
    : []),

  ...(hasLoiDetails ? loiDetails : []),
];

  const displayValue = (val: any) => {
    if (val === true) return " Yes";
    if (val === false) return " No";
    if (val === "" || val === null || val === undefined) return "NA";
    return val;
  };

  // const questionPairs: [Question, Question?][] = [];
  // for (let i = 0; i < questions.length; i += 2) {
  //   questionPairs.push([questions[i], questions[i + 1]]);
  // }

  const questionPairs: [Question, Question?][] = Array.isArray(questions)
    ? questions.reduce<[Question, Question?][]>((acc, curr, index) => {
      if (index % 2 === 0) {
        acc.push([curr]); // start new pair
      } else {
        acc[acc.length - 1].push(curr); // add second item to last pair
      }
      return acc;
    }, [])
    : [];

  const pairQuestions = (questionsArr: PreviewQuestion[]) => {
    const pairs = [];
    for (let i = 0; i < questionsArr.length; i += 2) {
      pairs.push([questionsArr[i], questionsArr[i + 1] || null]);
    }
    return pairs;
  };

  const questionPairsNew = pairQuestions(questionsArr);

  // Helper function to check if value should be displayed
  const shouldDisplayItem = (value: any) => {
    return (
      value !== "" && value !== null && value !== undefined && value !== "NA"
    );
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Card sx={{ boxShadow: "none", border: "1px solid #E7E8E9" }}>
        <Box
          sx={{
            backgroundColor: "#F4F2F7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pl: 2,
            borderBottom: "1px solid #E7E8E9",
            py: "5px",
          }}
        >
          <Typography
            sx={{ fontSize: 12 }}
            variant="h6"
            fontWeight="semi-bold"
            className="preview-dropdown-header"
          >
            Site & Highways Information{" "}
            {visible && (
              <IconButton
                className="ml-4"
                onClick={() => {
                  dispatch(setActiveStep(1));
                  dispatch(resetApplicationSubmitted());
                  navigate("/stepper");
                }}
              >
                <ModeEditOutlineOutlinedIcon />
              </IconButton>
            )}
          </Typography>
          <IconButton aria-label="Collapse section" onClick={toggleExpand}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        {/* Content */}
        {isExpanded && (
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {roadwayData?.map(
                (item, index) =>
                  item.label &&
                  // Add this condition to check if value is valid
                  shouldDisplayItem(item.value) && (
                    <Box
                      key={index}
                      sx={{
                        flex: "1 1 48%", // ~50% width with spacing
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        className="preview-dropdown-label"
                        variant="body2"
                      >
                        {displayValue(item.label)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        className="preview-dropdown-value"
                      >
                        {displayValue(item.value)}
                      </Typography>
                    </Box>
                  ),
              )}
            </Box>
            <Box>
              {questionPairsNew?.map((pair, idx) => (
                <Grid container spacing={2} key={idx} sx={{ py: 1 }}>
                  {pair[0]?.value !== "" && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          className="preview-dropdown-label"
                          gap={0.5}
                        >
                          {pair[0]?.question_name?.trim() || "—"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="preview-dropdown-value"
                        >
                          {displayValue(pair[0]?.value)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {pair[1]?.value !== "" && (
                    <Grid item xs={12} md={6}>
                      {pair[1] && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            className="preview-dropdown-label"
                          >
                            {pair[1].question_name?.trim()}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="preview-dropdown-value"
                          >
                            {displayValue(pair[1].value)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  )}
                </Grid>
              ))}
            </Box>
            <Box>
              {questionPairs?.map((pair, idx) => (
                <Grid container spacing={2} key={idx} sx={{ py: 1 }}>
                  {pair[0]?.value !== "" && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          className="preview-dropdown-label"
                          gap={0.5}
                        >
                          {pair[0]?.question_name?.trim() || "—"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="preview-dropdown-value"
                        >
                          {displayValue(pair[0]?.value)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {pair[1]?.value !== "" && (
                    <Grid item xs={12} md={6}>
                      {pair[1] && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            className="preview-dropdown-label"
                          >
                            {pair[1].question_name?.trim()}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="preview-dropdown-value"
                          >
                            {displayValue(pair[1].value)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  )}
                </Grid>
              ))}
            </Box>
          </CardContent>
        )}
      </Card>
    </Box>
  );
};
